import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import axiosInstance from "../axiosInstance";

const PAGE_SIZE = 10;

const mapProduct = (p) => ({
  id: p.Id ?? p.id,
  title: p.Title ?? p.title ?? "Untitled product",
  imageUrl: p.Images?.[0] || p.Variants?.[0]?.ImageUrl || "",
  link: p.Link ?? p.Variants?.[0]?.VariantUrl ?? "",
  productType: p.ProductType ?? "",
  price: p.Price ?? p.price ?? p.Variants?.[0]?.Price ?? "",
});

const ProductPickerDialog = ({ open, onClose, onSelect, tenantId, title = "Select a Product" }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchProducts = useCallback((tid, q, p) => {
    if (!tid) return;
    setLoading(true);
    axiosInstance
      .post(
        `/product/${tid}/search`,
        { query: q, productsPerPage: PAGE_SIZE, page: p, tenantId: tid },
        { headers: { TenantId: tid } }
      )
      .then(({ data }) => {
        const raw = data.products ?? data.items ?? [];
        setProducts(raw.map(mapProduct));
        setTotal(data.totalProducts ?? data.total ?? raw.length);
        setPage(p);
      })
      .catch(() => {
        setProducts([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, []);

  const debouncedFetch = useMemo(() => debounce(fetchProducts, 400), [fetchProducts]);
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setSearch("");
      fetchProducts(tenantId, "", 1);
    }
  }, [open, tenantId, fetchProducts]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedFetch(tenantId, value, 1);
  };

  const goToPage = (target) => fetchProducts(tenantId, search, target);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleConfirm = () => {
    if (selected) onSelect(selected);
  };

  const handleProductDoubleClick = (product) => {
    setSelected(product);
    onSelect(product);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          border: "1px solid rgba(255, 132, 76, 0.12)",
          background: "linear-gradient(180deg, rgba(255,248,243,0.98), rgba(255,255,255,0.96))",
          boxShadow: "0 30px 70px rgba(236,123,55,0.16)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,132,76,0.10)",
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Inventory2OutlinedIcon sx={{ color: "#f97316", fontSize: 22 }} />
          <Typography sx={{ fontWeight: 700, color: "#202531", fontSize: "1rem" }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={() => fetchProducts(tenantId, search, page)}
                disabled={loading}
                sx={{ color: "#9ca3af", "&:hover": { color: "#f97316" } }}
              >
                <RefreshOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "#6b7280", "&:hover": { color: "#f97316" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ px: 2.5, pt: 2, pb: 1.25 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search products…"
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#f97316" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setSearch(""); fetchProducts(tenantId, "", 1); }}>
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#fff",
              "& fieldset": { borderColor: "rgba(255,132,76,0.20)" },
              "&:hover fieldset": { borderColor: "#f97316" },
              "&.Mui-focused fieldset": { borderColor: "#f97316", borderWidth: "1px" },
            },
          }}
        />
      </Box>

      <DialogContent sx={{ px: 2.5, py: 0, maxHeight: 380, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: "#f97316" }} />
          </Box>
        ) : products.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1, color: "#9ca3af" }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 40, opacity: 0.35 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {search ? "No products match your search" : "No products found"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pb: 1 }}>
            {products.map((product) => {
              const isSelected = selected?.id === product.id;
              return (
                <Paper
                  key={product.id}
                  elevation={0}
                  onClick={() => setSelected(isSelected ? null : product)}
                  onDoubleClick={() => handleProductDoubleClick(product)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.1,
                    borderRadius: "12px",
                    cursor: "pointer",
                    border: `1.5px solid ${isSelected ? "rgba(249,115,22,0.50)" : "rgba(255,132,76,0.10)"}`,
                    backgroundColor: isSelected ? "rgba(255,241,233,0.96)" : "rgba(255,255,255,0.82)",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: "rgba(249,115,22,0.35)",
                      backgroundColor: "rgba(255,247,241,0.92)",
                    },
                  }}
                >
                  {product.imageUrl ? (
                    <Box
                      component="img"
                      src={product.imageUrl}
                      alt={product.title}
                      sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: "8px", flexShrink: 0 }}
                      onError={(e) => { e.target.style.visibility = "hidden"; }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255,132,76,0.08)",
                        color: "#f97316",
                        flexShrink: 0,
                      }}
                    >
                      <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#202531",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {product.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#9ca3af", fontSize: "0.72rem", fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {product.id}
                      {product.productType ? ` · ${product.productType}` : ""}
                    </Typography>
                  </Box>

                  {isSelected && (
                    <CheckCircleOutlinedIcon sx={{ color: "#f97316", fontSize: 20, flexShrink: 0 }} />
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {total > PAGE_SIZE && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, px: 2.5, py: 1, borderTop: "1px solid rgba(255,132,76,0.08)" }}>
          <Button
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => goToPage(page - 1)}
            sx={{ color: "#f97316", textTransform: "none", fontWeight: 600 }}
          >
            Previous
          </Button>
          <Typography variant="caption" sx={{ color: "#6b7280" }}>
            Page {page} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => goToPage(page + 1)}
            sx={{ color: "#f97316", textTransform: "none", fontWeight: 600 }}
          >
            Next
          </Button>
        </Box>
      )}

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: "#6b7280", textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected}
          onClick={handleConfirm}
          sx={{
            background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
            boxShadow: "0 14px 30px rgba(255,139,71,0.28)",
            borderRadius: "14px",
            textTransform: "none",
            fontWeight: 700,
            px: 2.5,
            "&:hover": { background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)" },
            "&:disabled": { background: "linear-gradient(180deg, #ffd4b5 0%, #ffb880 100%)", boxShadow: "none", color: "rgba(255,255,255,0.7)" },
          }}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProductPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  tenantId: PropTypes.string,
  title: PropTypes.string,
};

export default ProductPickerDialog;
